/**
 * Dev-only mock data. When VITE_MOCK=1, the API client returns these instead of
 * hitting the backend — handy for local UI work + screenshots without a running
 * server. Never enabled in production builds.
 */

export const MOCK_ENABLED = import.meta.env.VITE_MOCK === '1';

const trend = Array.from({ length: 14 }, (_, i) => ({
  date: `05-${String(i + 16).padStart(2, '0')}`,
  high: Math.round(2 + Math.sin(i / 2) * 2 + Math.random() * 2),
  medium: Math.round(4 + Math.cos(i / 3) * 3 + Math.random() * 2),
  low: Math.round(8 + Math.sin(i / 4) * 4 + Math.random() * 3),
}));

const PROFILE = {
  shopDomain: 'demo-store.myshopify.com',
  plan: 'growth',
  whatsappStatus: 'connected',
  whatsappPhone: '+966 50 000 0000',
  whatsappError: null,
  primaryLanguage: 'en',
  currency: 'SAR',
  country: 'Saudi Arabia',
  timezone: 'Asia/Riyadh',
  installedAt: new Date(Date.now() - 30 * 86400_000).toISOString(),
  onboarding: {
    completed: true,
    whatsappConnected: true,
    preferencesSet: true,
    featuresConfigured: true,
  },
};

const WHATSAPP_STATUS = {
  status: 'connected',
  displayPhone: '+966 50 000 0000',
  errorReason: null,
  verifiedAt: new Date(Date.now() - 30 * 86400_000).toISOString(),
};

const SETTINGS = {
  settings: {
    dailyMessageLimit: 200,
    workingHours: { start: '09:00', end: '21:00', tz: 'Asia/Riyadh', respectPrayerTimes: true },
    riskRules: { autoBlock: false, minScoreToAlert: 70, autoCodToPrepaidThreshold: 75 },
    enabledFeatures: {
      riskScoring: true,
      twoWayAI: true,
      addressValidation: true,
      networkLookup: false,
      codToPrepaid: true,
    },
    notifications: { email: 'owner@demo-store.com', dailyDigest: true, instantHighRisk: true },
    languages: ['en', 'ar'],
    primaryLanguage: 'en',
  },
};

const STATS = {
  totalOrders: 312,
  highRiskOrders: 28,
  messagesSent: 1184,
  codToPrepaidConversions: 41,
  estimatedSavings: 18640,
  currency: 'SAR',
  ordersByStatus: {
    confirmation_pending: 64,
    confirmed: 38,
    dispatching: 22,
    shipped: 71,
    undelivered: 19,
    delivered: 86,
    cancelled: 12,
  },
  riskTrend: trend,
};

const NAMES = [
  'Alauddin',
  'أم خالد',
  'Arun',
  'Grace Malaga',
  'مصطفى سلامة',
  'Gayathri Mohan',
  'NAHID',
  'Rima',
];
const CITIES = ['Jeddah', 'Riyadh', 'Makkah', 'Khafji', 'Madinah', 'Abha', 'Dammam', 'Tabuk'];
const STATUSES = [
  'confirmation_pending',
  'shipped',
  'undelivered',
  'delivered',
  'cancelled',
  'dispatching',
];
const LEVELS = ['low', 'medium', 'high'];

const ORDERS = Array.from({ length: 24 }, (_, i) => {
  const score = Math.round(Math.random() * 100);
  const level = score >= 70 ? 'high' : score >= 40 ? 'medium' : 'low';
  return {
    id: `ord_${1000 + i}`,
    orderNumber: `#${1040 - i}`,
    customerName: NAMES[i % NAMES.length],
    city: CITIES[i % CITIES.length],
    total: 99 + (i % 7) * 40,
    currency: 'SAR',
    paymentMethod: i % 3 === 0 ? 'prepaid' : 'cod',
    status: STATUSES[i % STATUSES.length],
    riskScore: score,
    riskLevel: LEVELS.indexOf(level) >= 0 ? level : 'low',
    createdAt: new Date(Date.now() - i * 3600_000).toISOString(),
  };
});

const PLANS = {
  currentPlan: 'growth',
  usage: 1184,
  plans: [
    {
      plan: 'free',
      name: 'Free',
      priceUsd: 0,
      monthlyMessages: 50,
      features: {
        riskScoring: false,
        twoWayAI: false,
        addressValidation: false,
        networkLookup: false,
        codToPrepaid: false,
      },
    },
    {
      plan: 'starter',
      name: 'Starter',
      priceUsd: 29,
      monthlyMessages: 500,
      features: {
        riskScoring: true,
        twoWayAI: false,
        addressValidation: true,
        networkLookup: false,
        codToPrepaid: false,
      },
    },
    {
      plan: 'growth',
      name: 'Growth',
      priceUsd: 79,
      monthlyMessages: 2000,
      features: {
        riskScoring: true,
        twoWayAI: true,
        addressValidation: true,
        networkLookup: false,
        codToPrepaid: false,
      },
    },
    {
      plan: 'pro',
      name: 'Pro',
      priceUsd: 199,
      monthlyMessages: null,
      features: {
        riskScoring: true,
        twoWayAI: true,
        addressValidation: true,
        networkLookup: true,
        codToPrepaid: true,
      },
    },
  ],
};

const CONVERSATIONS = {
  conversations: [
    {
      id: 'c1',
      customerPhone: '+966500000001',
      currentIntent: 'confirm',
      aiHandling: true,
      outcome: null,
      lastTurnAt: new Date(Date.now() - 600_000).toISOString(),
      lastMessage: 'Yes please deliver tomorrow morning',
      turnCount: 4,
    },
    {
      id: 'c2',
      customerPhone: '+966500000002',
      currentIntent: 'reschedule',
      aiHandling: true,
      outcome: null,
      lastTurnAt: new Date(Date.now() - 3_600_000).toISOString(),
      lastMessage: 'Can you deliver after 5pm?',
      turnCount: 6,
    },
    {
      id: 'c3',
      customerPhone: '+966500000003',
      currentIntent: 'complaint',
      aiHandling: false,
      outcome: 'escalated',
      lastTurnAt: new Date(Date.now() - 7_200_000).toISOString(),
      lastMessage: 'The item looks different from the photo',
      turnCount: 8,
    },
    {
      id: 'c4',
      customerPhone: '+966500000004',
      currentIntent: 'confirm',
      aiHandling: true,
      outcome: 'confirmed',
      lastTurnAt: new Date(Date.now() - 86_400_000).toISOString(),
      lastMessage: 'Location shared ✅',
      turnCount: 3,
    },
  ],
};

const PORTAL_USER = {
  id: 'u1',
  email: 'owner@demo-store.com',
  name: 'Demo Merchant',
  emailVerified: true,
};

const PORTAL_STORES = {
  stores: [
    {
      id: 'ord_seller_1',
      shopDomain: 'demo-store.myshopify.com',
      plan: 'growth',
      role: 'owner',
      whatsappStatus: 'connected',
      lastSelectedAt: new Date().toISOString(),
      uninstalled: false,
    },
    {
      id: 'ord_seller_2',
      shopDomain: 'second-shop.myshopify.com',
      plan: 'starter',
      role: 'owner',
      whatsappStatus: 'not_connected',
      lastSelectedAt: null,
      uninstalled: false,
    },
  ],
  storeLimit: 3,
  canConnectMore: true,
};

const BILLING_HISTORY = {
  events: [
    {
      id: 'b1',
      type: 'upgraded',
      plan: 'growth',
      amountUsd: 79,
      occurredAt: new Date(Date.now() - 2 * 86400_000).toISOString(),
      note: 'approved via Shopify',
    },
    {
      id: 'b2',
      type: 'activated',
      plan: 'starter',
      amountUsd: 29,
      occurredAt: new Date(Date.now() - 32 * 86400_000).toISOString(),
      note: null,
    },
  ],
};

const TEMPLATES = {
  templates: [
    {
      id: 't1',
      name: 'Default confirmation_pending',
      status: 'confirmation_pending',
      language: 'en',
      body: 'Hello {customer_name}! 👋 Thank you for your order #{order_id}. To arrange delivery, please share your location pin so our courier can reach you accurately.',
      isActive: true,
      version: 1,
      approval: 'local',
      rejectionReason: null,
      updatedAt: new Date(Date.now() - 5 * 86400_000).toISOString(),
    },
    {
      id: 't2',
      name: 'Default shipped',
      status: 'shipped',
      language: 'en',
      body: 'Your order #{order_id} is on its way, {customer_name}! 🚚 Tracking: {tracking}.',
      isActive: true,
      version: 2,
      approval: 'approved',
      rejectionReason: null,
      updatedAt: new Date(Date.now() - 2 * 86400_000).toISOString(),
    },
    {
      id: 't3',
      name: 'Arabic confirmation',
      status: 'confirmation_pending',
      language: 'ar',
      body: 'مرحباً {customer_name}! شكراً لطلبك #{order_id}. يرجى مشاركة موقعك لتسهيل التوصيل.',
      isActive: false,
      version: 1,
      approval: 'pending',
      rejectionReason: null,
      updatedAt: new Date(Date.now() - 86400_000).toISOString(),
    },
  ],
};

const REPORT = {
  from: '2026-04-30',
  to: '2026-05-30',
  currency: 'SAR',
  totals: {
    orders: 312,
    highRisk: 28,
    codOrders: 214,
    prepaidOrders: 98,
    conversions: 41,
    revenue: 64820,
    estimatedSavings: 18640,
  },
  byStatus: [
    { key: 'delivered', orders: 86, highRisk: 2, revenue: 21400 },
    { key: 'shipped', orders: 71, highRisk: 6, revenue: 17800 },
    { key: 'confirmation_pending', orders: 64, highRisk: 12, revenue: 9200 },
    { key: 'confirmed', orders: 38, highRisk: 3, revenue: 8600 },
    { key: 'undelivered', orders: 19, highRisk: 4, revenue: 3100 },
    { key: 'cancelled', orders: 12, highRisk: 1, revenue: 0 },
  ],
  byCity: [
    { key: 'Jeddah', orders: 72, highRisk: 5, revenue: 15200 },
    { key: 'Riyadh', orders: 64, highRisk: 4, revenue: 13800 },
    { key: 'Makkah', orders: 41, highRisk: 6, revenue: 7900 },
    { key: 'Khafji', orders: 18, highRisk: 8, revenue: 2100 },
    { key: 'Dammam', orders: 33, highRisk: 2, revenue: 6400 },
  ],
  byPayment: [
    { key: 'cod', orders: 214, highRisk: 26, revenue: 41200 },
    { key: 'prepaid', orders: 98, highRisk: 2, revenue: 23620 },
  ],
};

/** Return mock data for a path, or undefined if unmocked. */
export function mockResponse(path: string): unknown {
  if (path === '/auth/portal/me') return PORTAL_USER;
  if (path === '/api/templates') return TEMPLATES;
  if (path.startsWith('/api/reports')) return REPORT;
  if (path === '/portal/stores') return PORTAL_STORES;
  if (path === '/api/billing/history') return BILLING_HISTORY;
  if (path === '/api/me') return PROFILE;
  if (path === '/api/stats') return STATS;
  if (path === '/api/billing/plans')
    return {
      ...PLANS,
      renewalDate: new Date(Date.now() + 12 * 86400_000).toISOString(),
      daysLeft: 12,
      quota: 2000,
    };
  if (path === '/api/settings') return SETTINGS;
  if (path === '/api/whatsapp/status') return WHATSAPP_STATUS;
  if (path === '/api/conversations') return CONVERSATIONS;
  if (/^\/api\/conversations\/[^/]+$/.test(path)) {
    const id = path.split('/').pop();
    const base = CONVERSATIONS.conversations.find((c) => c.id === id) ??
      CONVERSATIONS.conversations[0] ?? {
        id: 'c1',
        customerPhone: '+966500000000',
        currentIntent: 'confirm',
        aiHandling: true,
        outcome: null,
        lastMessage: 'Yes please',
      };
    return {
      conversation: {
        id: base.id,
        customerPhone: base.customerPhone,
        currentIntent: base.currentIntent,
        aiHandling: base.aiHandling,
        outcome: base.outcome,
        turns: [
          {
            role: 'ai',
            text: 'Hello! Your order is ready. Please share your location pin so our courier can reach you.',
            timestamp: new Date(Date.now() - 7_200_000).toISOString(),
          },
          {
            role: 'customer',
            text: base.lastMessage,
            timestamp: new Date(Date.now() - 6_900_000).toISOString(),
          },
          {
            role: 'ai',
            text: 'Great! We will deliver tomorrow morning. Thank you 😊',
            timestamp: new Date(Date.now() - 6_800_000).toISOString(),
          },
        ],
      },
    };
  }
  if (path.startsWith('/api/orders/') && !path.includes('?')) {
    const id = path.split('/').pop();
    const found = ORDERS.find((o) => o.id === id) ?? ORDERS[0];
    return {
      order: {
        ...found,
        riskReasons: ['Cash on delivery', 'Incomplete street address', 'No prior history'],
        addressScore: 55,
        addressIssues: ['Street address too short'],
        customerPhone: '+966500000000',
        addressLines: ['Nuzla kilo 3, building 12'],
        reputation: { trustScore: 62, totalOrders: 4, flagged: 0 },
        messages: [
          {
            id: 'm1',
            direction: 'out',
            bodyText:
              'Hello! Your order is ready. Please share your location pin to arrange delivery.',
            status: 'read',
            at: new Date(Date.now() - 7_200_000).toISOString(),
          },
          {
            id: 'm2',
            direction: 'in',
            bodyText: 'Yes please, deliver tomorrow morning',
            status: 'delivered',
            at: new Date(Date.now() - 6_900_000).toISOString(),
          },
          {
            id: 'm3',
            direction: 'out',
            bodyText: 'Great! We will deliver tomorrow. Thank you 😊',
            status: 'delivered',
            at: new Date(Date.now() - 6_800_000).toISOString(),
          },
        ],
      },
    };
  }
  if (path.startsWith('/api/orders')) {
    const status = new URLSearchParams(path.split('?')[1] ?? '').get('status');
    const list = status ? ORDERS.filter((o) => o.status === status) : ORDERS;
    return { orders: list };
  }
  return undefined;
}
